import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { XR_EXPERIENCES } from '../data/xr-experiences';
import { fetchExperiencesFromFirestore, canUserCreatePost, canUserReview } from '../services/experiencesFirestore';

const ExperiencesContext = createContext(null);

export function ExperiencesProvider({ children }) {
  const { user } = useAuth();
  const [firestoreExperiences, setFirestoreExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canCreatePost, setCanCreatePost] = useState(false);
  const [canReview, setCanReview] = useState(false);

  const loadExperiences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchExperiencesFromFirestore();
      setFirestoreExperiences(list);
    } catch (err) {
      setError(err.message || 'Failed to load experiences');
      setFirestoreExperiences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  useEffect(() => {
    if (!user) {
      setCanCreatePost(false);
      setCanReview(false);
      return;
    }
    let cancelled = false;
    Promise.all([canUserCreatePost(user.uid), canUserReview(user.uid)]).then(([create, review]) => {
      if (!cancelled) {
        setCanCreatePost(create);
        setCanReview(review);
      }
    });
    return () => { cancelled = true; };
  }, [user]);

  // Static curated list is source of truth; add Firestore-only (user-created) experiences on top
  const experiences = useMemo(() => {
    const staticIds = new Set(XR_EXPERIENCES.map((e) => e.id));
    const list = [...XR_EXPERIENCES];
    firestoreExperiences.forEach((e) => {
      if (!staticIds.has(e.id) && e.status !== 'pending') list.push(e);
    });
    return list;
  }, [firestoreExperiences]);

  const getExperienceById = useCallback(
    (id) => experiences.find((exp) => exp.id === id) || null,
    [experiences]
  );

  const value = useMemo(
    () => ({
      experiences,
      loading,
      error,
      getExperienceById,
      canCreatePost,
      canReview,
      refetch: loadExperiences
    }),
    [experiences, loading, error, getExperienceById, canCreatePost, canReview, loadExperiences]
  );

  return (
    <ExperiencesContext.Provider value={value}>
      {children}
    </ExperiencesContext.Provider>
  );
}

export function useExperiences() {
  const ctx = useContext(ExperiencesContext);
  if (!ctx) throw new Error('useExperiences must be used within ExperiencesProvider');
  return ctx;
}
